/**
 * Loads the Google Maps JS SDK on demand.
 *
 * The SDK used to be a blocking `<script>` in `public/index.html`, so it was
 * fetched on EVERY page load of the app — `/taskscn`, `/portfolios`, `/admin`,
 * screens with no address input anywhere. Google bills Dynamic Maps / Places
 * SDK **per page load**, not per autocomplete, so the majority of that spend
 * bought nothing (DT-012).
 *
 * Address entry is the only thing that needs it: `AddressAutocomplete` and the
 * `react-places-autocomplete` / `use-places-autocomplete` call sites. Those
 * screens now pull the SDK in when they mount.
 *
 * The loader is idempotent and safe to call from several components at once —
 * concurrent callers share one in-flight promise, and a second call after a
 * successful load resolves immediately.
 *
 * Note `functions/geocodeAddress.js` does NOT need this: it calls the Geocoding
 * REST endpoint over `fetch` and never touches the SDK.
 */

const SCRIPT_ID = 'google-maps-sdk'

type GoogleNamespace = any

let loadPromise: Promise<GoogleNamespace | null> | null = null

/** True once `google.maps.places` is usable. */
export const isGoogleMapsReady = (): boolean => {
	const g = (window as any).google
	return Boolean(g?.maps?.places)
}

/** The loaded namespace, or null if it is not ready yet. */
export const getGoogleMaps = (): GoogleNamespace | null =>
	isGoogleMapsReady() ? (window as any).google : null

/**
 * Injects the SDK if it is not already present and resolves when it is usable.
 *
 * Resolves with `null` rather than rejecting when no API key is configured or
 * the script fails to load — callers degrade to a plain text input, which is
 * how the component behaved before this existed when the script was blocked.
 */
export const loadGoogleMaps = (): Promise<GoogleNamespace | null> => {
	if (isGoogleMapsReady()) return Promise.resolve(getGoogleMaps())
	if (loadPromise) return loadPromise

	const key = process.env.REACT_APP_GOOGLE_API_KEY

	if (!key) {
		// Not fatal: address fields fall back to free text. Warn once so the
		// cause is discoverable rather than silent.
		// eslint-disable-next-line no-console
		console.warn(
			'[loadGoogleMaps] REACT_APP_GOOGLE_API_KEY is not set — ' +
				'address autocomplete will fall back to plain text entry.',
		)
		loadPromise = Promise.resolve(null)
		return loadPromise
	}

	loadPromise = new Promise(resolve => {
		const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null

		if (existing) {
			// Another caller began the injection; wait for that same element.
			existing.addEventListener('load', () => resolve(getGoogleMaps()))
			existing.addEventListener('error', () => resolve(null))
			return
		}

		const script = document.createElement('script')
		script.id = SCRIPT_ID
		script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
			key,
		)}&libraries=places`
		script.async = true
		script.defer = true

		script.addEventListener('load', () => resolve(getGoogleMaps()))
		script.addEventListener('error', () => {
			// eslint-disable-next-line no-console
			console.warn('[loadGoogleMaps] the Google Maps SDK failed to load.')
			// Allow a later retry rather than caching the failure forever.
			loadPromise = null
			resolve(null)
		})

		document.head.appendChild(script)
	})

	return loadPromise
}

export default loadGoogleMaps
